import { useParams, Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { shortAddr, fmtNum } from "../lib/format";
import { SectionHead, DataBadge, Skel, Empty, CopyBtn } from "../components/common";

const Row = ({ label, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-center px-3 py-2.5 border-b border-[#1a1a1a] last:border-0 text-[13px]">
    <span className="w-44 text-[#6B7280] shrink-0">{label}</span>
    <span className="num break-all">{children}</span>
  </div>
);

export default function ExplorerTx() {
  const { hash } = useParams();
  const { data, loading } = useApi(`/explorer/tx/${hash}`);

  if (loading) return <Skel rows={10} h={36} />;
  if (!data?.live || !data.tx) return <Empty msg="Transaction not found on Robinhood Chain (or live data unavailable)." />;
  const tx = data.tx;
  const ok = tx.status === "ok";
  const value = parseInt(tx.value || "0") / 1e18;
  const fee = parseInt(tx.fee?.value || "0") / 1e18;
  const summary = data.token_transfers?.length
    ? `Wallet ${shortAddr(tx.from?.hash || "")} transferred ${data.token_transfers.length} token asset(s)${tx.to?.name ? ` via ${tx.to.name}` : ""} in this transaction.`
    : value > 0
      ? `Wallet ${shortAddr(tx.from?.hash || "")} sent ${value.toFixed(6)} ETH to ${shortAddr(tx.to?.hash || "")}.`
      : `Wallet ${shortAddr(tx.from?.hash || "")} interacted with contract ${shortAddr(tx.to?.hash || "")}${tx.method ? ` calling ${tx.method}` : ""}.`;

  return (
    <div className="space-y-4">
      <SectionHead title="Transaction" badge={<DataBadge live />} />
      <div className="t-card p-3 text-[13px] text-[#9CA3AF]" data-testid="tx-summary">
        <span className="text-[10px] uppercase text-[#6B7280] block mb-1">Human-readable summary</span>{summary}
      </div>
      <div className="t-card" data-testid="tx-details">
        <Row label="Status"><span className={ok ? "text-[#10B981]" : "text-[#EF4444]"}>{ok ? "Success" : tx.status || "Pending"}</span></Row>
        <Row label="Hash">{tx.hash} <CopyBtn text={tx.hash} /></Row>
        <Row label="Block"><Link to={`/explorer/block/${tx.block_number ?? tx.block}`} className="text-[#10B981] hover:underline">#{tx.block_number ?? tx.block}</Link></Row>
        <Row label="Timestamp">{tx.timestamp ? new Date(tx.timestamp).toLocaleString() : "—"}</Row>
        <Row label="From"><Link to={`/wallet/${tx.from?.hash}`} className="text-[#10B981] hover:underline">{tx.from?.hash}</Link></Row>
        <Row label="To"><Link to={`/wallet/${tx.to?.hash}`} className="text-[#10B981] hover:underline">{tx.to?.hash}</Link>{tx.to?.name ? ` (${tx.to.name})` : ""}</Row>
        <Row label="Value">{value.toFixed(6)} ETH</Row>
        <Row label="Fee">{fee.toFixed(8)} ETH</Row>
        <Row label="Gas Used">{fmtNum(parseInt(tx.gas_used || 0))}</Row>
        <Row label="Gas Price">{tx.gas_price ? `${(parseInt(tx.gas_price) / 1e9).toFixed(4)} gwei` : "—"}</Row>
        <Row label="Method">{tx.method || "transfer"}</Row>
        <Row label="Nonce">{tx.nonce}</Row>
      </div>
      {data.token_transfers?.length > 0 && (
        <>
          <SectionHead title="Token Transfers" />
          <div className="t-card">
            {data.token_transfers.map((tt, i) => (
              <div key={i} className="px-3 py-2 border-b border-[#1a1a1a] last:border-0 text-[13px] flex flex-wrap gap-2">
                <span className="num text-[#6B7280]">{shortAddr(tt.from?.hash || "")}</span>→
                <span className="num text-[#6B7280]">{shortAddr(tt.to?.hash || "")}</span>
                <span className="num ml-auto">{tt.total?.value ? (parseInt(tt.total.value) / 10 ** (parseInt(tt.token?.decimals) || 18)).toLocaleString() : ""} {tt.token?.symbol}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {data.logs?.length > 0 && (
        <>
          <SectionHead title="Event Logs" />
          <div className="t-card">
            {data.logs.map((l, i) => (
              <div key={i} className="px-3 py-2 border-b border-[#1a1a1a] last:border-0 text-xs">
                <span className="num text-[#6B7280]">{shortAddr(l.address?.hash || "")}</span>
                <span className="ml-2 text-[#9CA3AF]">{l.decoded?.method_call || l.topics?.[0]?.slice(0, 20) || "log"}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
